import { z } from "@reflag/shared/utils/zod";

import { APICollectionResponse, APIEmptyResponse, APIResponse } from "./api";
import {
  EnvironmentDTO,
  EnvironmentOpenApiSchema,
  EnvironmentSchema,
} from "./environmentAPI";
import { nameSchema } from "./schemas/nameSchema";
import {
  AppHeaderSchema,
  CollectionSchema,
  OrgIdSchema,
  SegmentHeaderSchema,
  SlackChannelSchema,
  StageHeaderSchema,
} from "./schemas/shared";
import { SlackChannel } from "./slackConnectionAPI";

export const AppSchema = AppHeaderSchema.extend({
  environments: z.array(EnvironmentSchema).meta({
    description: "Environments within the app",
  }),
  stages: z.array(StageHeaderSchema).meta({
    description: "Stages within the app",
  }),
  segments: z.array(SegmentHeaderSchema).meta({
    description: "Segments within the app",
  }),
}).meta({
  id: "appDetails",
  description: "App information with related collections",
});

// Intentionally slimmer than AppSchema: public OpenAPI docs for GET /apps/:appId
// expose a reduced environment payload, while internal runtime DTOs keep full
// environment fields for app/bootstrap flows.
export const AppOpenApiSchema = AppHeaderSchema.extend({
  environments: z.array(EnvironmentOpenApiSchema).meta({
    description: "Environments within the app",
  }),
  stages: z.array(StageHeaderSchema).meta({
    description: "Stages within the app",
  }),
  segments: z.array(SegmentHeaderSchema).meta({
    description: "Segments within the app",
  }),
}).meta({
  id: "app",
  description: "App information with related collections",
});

export const AppCollectionSchema = CollectionSchema(AppHeaderSchema);

export type AppHeaderDTO = z.output<typeof AppHeaderSchema>;
export type AppDetailDTO = z.output<typeof AppSchema>;

// Compatibility type used by frontend helpers that may receive either full app
// details or bootstrap app objects (which omit stages/segments and may include
// app-level integrations).
export type AppDTO = Omit<AppHeaderDTO, "environments"> & {
  environments: EnvironmentDTO[];
  stages?: z.output<typeof StageHeaderSchema>[];
  segments?: z.output<typeof SegmentHeaderSchema>[];
  integrations?: {
    slack?: {
      channel: SlackChannel | null;
    };
  };
};

export const CreateAppArgsSchema = z
  .object({
    orgId: z.string(),
    name: nameSchema,
    demoData: z.boolean().optional().default(false),
  })
  .strict();

export const CreateAppWithOrgArgsSchema = z
  .object({
    name: nameSchema,
  })
  .strict();

export type CreateAppArgsType = z.input<typeof CreateAppArgsSchema>;

export const PatchAppArgsSchema = CreateAppArgsSchema.pick({
  name: true,
})
  .extend({
    slackChannel: SlackChannelSchema.nullish(),
  })
  .partial();

export type PatchAppArgsType = z.input<typeof PatchAppArgsSchema>;

export const GetAppsQuerySchema = z
  .object({
    orgId: OrgIdSchema.optional(),
  })
  .meta({
    id: "getAppsQuery",
    description: "Query parameters for getting a list of basic apps details",
  });

export interface AppAPI {
  "/apps": {
    POST: {
      response: APIResponse<AppDetailDTO>;
      body: CreateAppArgsType;
      query: z.input<typeof GetAppsQuerySchema>;
    };
    GET: {
      response: APICollectionResponse<AppHeaderDTO>;
      query: z.input<typeof GetAppsQuerySchema>;
    };
  };
  "/apps/:appId": {
    GET: {
      response: APIResponse<AppDetailDTO>;
      params: {
        appId: string;
      };
    };
    PATCH: {
      response: APIResponse<AppDetailDTO>;
      params: {
        appId: string;
      };
      body: PatchAppArgsType;
    };
    DELETE: {
      response: APIEmptyResponse;
      params: {
        appId: string;
      };
    };
  };
}
